import { Chip, ScrollShadow, Spinner } from '@heroui/react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { $fetch } from 'utils/fetch'

function formatPayload({ event_type, payload }) {
  const {
    call_id,
    reason,
    phone_number,
    state_change,
    message,
    code,
    calls_successful,
    calls_failed,
  } = payload

  switch (event_type) {
    case 'lifecycle':
      return message ?? `Batch status changed to ${state_change}`
    case 'call_failure_validation':
      return `Call ${call_id} failed validation: ${reason}`
    case 'call_failure':
      return `Call ${call_id} failed: ${reason}`
    case 'call_failure_dispatch':
      return `Call to ${phone_number} failed to dispatch: ${reason}`
    case 'call_completed':
      return `Call ${call_id} completed${reason ? `: ${reason}` : ''}`
    case 'failed':
      return `Batch failed with code ${code}: ${reason}`
    case 'complete':
      return `Batch completed with ${calls_successful} successful calls and ${calls_failed} failed calls`
  }
}

const eventTypeMap = {
  lifecycle: { label: 'Lifecycle', color: 'primary' },
  call_failure_validation: { label: 'Call Validation Failure', color: 'warning' },
  call_failure: { label: 'Call Failure', color: 'danger' },
  call_failure_dispatch: { label: 'Call Dispatch Failure', color: 'danger' },
  call_completed: { label: 'Call Completed', color: 'success' },
  failed: { label: 'Batch Failed', color: 'danger' },
  complete: { label: 'Batch Completed', color: 'success' },
}

export default function LogStream({ batchId, isLive }) {
  const containerRef = useRef(null)

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['batches', batchId, 'logs'],
    queryFn: async ({ pageParam }) => {
      const { data } = await $fetch(`/v2/batches/${batchId}/logs`, {
        query: {
          skip: (pageParam - 1) * 20,
          take: 20,
        },
      })
      return data ?? []
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage.length === 0) {
        return undefined
      }
      return lastPageParam + 1
    },
    getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
      if (firstPageParam <= 1) {
        return undefined
      }
      return firstPageParam - 1
    },
    refetchInterval: isLive ? 2000 : false,
    enabled: !!batchId,
  })

  const logs = useMemo(
    () => data?.pages?.flatMap(page => page) ?? [],
    [data],
  )

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement

        if (
          scrollHeight - scrollTop - clientHeight < 150
          && !isFetchingNextPage
          && hasNextPage
        ) {
          fetchNextPage()
        }
      }
    },
    [fetchNextPage, isFetchingNextPage, hasNextPage],
  )

  // Check if we need to fetch more data on mount or after a fetch
  useEffect(() => {
    fetchMoreOnBottomReached(containerRef.current)
  }, [fetchMoreOnBottomReached])

  return (
    <ScrollShadow ref={containerRef} className="max-h-96 w-full flex flex-col gap-2 px-12">
      {(isLoading || isLive) && (
        <div className="flex justify-center w-full">
          <Spinner variant="dots" size="sm" />
        </div>
      )}
      {!isLoading && (
        <>
          {logs?.length > 0
            ? (
                <ul className="flex flex-col gap-1">
                  {logs.map((log) => {
                    const eventType = eventTypeMap[log.event_type]
                    return (
                      <li key={log.id} className="grid grid-cols-[9rem_auto_11rem] gap-1 items-center">
                        <Chip variant="flat" radius="sm" size="sm" color={eventType?.color || 'default'} classNames={{ content: 'w-32', dot: 'animate-pulse' }}>
                          {eventType?.label || 'Unknown'}
                        </Chip>
                        <p className="text-xs text-gray-500">{formatPayload(log)}</p>
                        <p className="text-xs text-gray-500 ml-auto font-mono">{log.timestamp?.split('T').join(' ').replace('Z', '')}</p>
                      </li>
                    )
                  })}
                </ul>
              )
            : (
                <div className="flex justify-center items-center h-full">
                  <p className="text-sm">No logs found</p>
                </div>
              )}
        </>
      )}
    </ScrollShadow>
  )
}
